import { COLORS } from "../shared/constants";
import { IconSearch, IconChevron, IconX, IconLock } from "../shared/icons";
import { useState } from "react";

export default function AssignTreatmentForm({ protocols, diagnoses, coils, treatmentSpots, mtSpots, patient, currentUser, onAssign, onCancel }) {
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [txCoil, setTxCoil] = useState("");
  const [txSpot, setTxSpot] = useState("");
  const [mtCoil, setMtCoil] = useState("");
  const [mtSpotVal, setMtSpotVal] = useState("");
  const [diagCode, setDiagCode] = useState(patient.diagnosis_code || "");
  const [plannedSessions, setPlannedSessions] = useState(36);
  const [mdApproval, setMdApproval] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("sort_order");
  const [sortDir, setSortDir] = useState("asc");
  const [errors, setErrors] = useState({});

  // Auto-populate coil/spot from selected protocol
  const handleSelectProtocol = (p) => {
    setSelectedProtocol(p);
    setTxCoil(p.treatment_coil || "");
    setTxSpot(p.treatment_spot || "");
    setMtCoil(p.mt_coil || "");
    setMtSpotVal(p.mt_spot || "");
    setErrors((prev) => ({ ...prev, protocol: null }));
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = protocols
    .filter((p) => p.is_active !== false)
    .filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "name": aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
        case "treatment_type": aVal = a.treatment_type; bVal = b.treatment_type; break;
        case "treatment_ratio_pct": aVal = a.treatment_ratio_pct; bVal = b.treatment_ratio_pct; break;
        case "frequency_hz": aVal = a.frequency_hz; bVal = b.frequency_hz; break;
        case "total_pulses": aVal = a.total_pulses; bVal = b.total_pulses; break;
        case "fda_approved": aVal = a.fda_approved ? 1 : 0; bVal = b.fda_approved ? 1 : 0; break;
        case "sort_order": aVal = a.sort_order ?? 100; bVal = b.sort_order ?? 100; break;
        default: aVal = a.sort_order ?? 100; bVal = b.sort_order ?? 100;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const handleSubmit = () => {
    const errs = {};
    if (!selectedProtocol) errs.protocol = "Select a protocol";
    if (!txCoil) errs.txCoil = "Required";
    if (!txSpot) errs.txSpot = "Required";
    if (!mtCoil) errs.mtCoil = "Required";
    if (!mtSpotVal) errs.mtSpot = "Required";
    if (!diagCode) errs.diagnosis = "Required";
    if (!plannedSessions || plannedSessions < 1) errs.sessions = "Required";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onAssign({
      patient_id: patient.id,
      protocol_id: selectedProtocol.id,
      treatment_coil: txCoil,
      treatment_spot: txSpot,
      mt_coil: mtCoil,
      mt_spot: mtSpotVal,
      diagnosis_code: diagCode,
      planned_sessions: parseInt(plannedSessions),
      md_approval_confirmed: currentUser.role === "MD" || mdApproval,
    });
  };

  const SortTh = ({ field, label, width }) => (
    <th onClick={() => handleSort(field)}
      style={{
        padding: "8px 10px", textAlign: "left", fontSize: "11px", fontWeight: "600",
        color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.3px",
        cursor: "pointer", userSelect: "none", borderBottom: `2px solid ${COLORS.border}`,
        width: width || "auto", whiteSpace: "nowrap",
        background: sortField === field ? COLORS.primaryLight : "transparent",
      }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
        {label}
        {sortField === field && <IconChevron dir={sortDir === "asc" ? "up" : "down"} />}
      </span>
    </th>
  );

  return (
    <div style={{ padding: "16px", background: COLORS.primaryLight, borderRadius: "10px", border: `1px solid ${COLORS.primary}30`, marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <div style={{ fontSize: "14px", fontWeight: "700", color: COLORS.primary }}>Assign Treatment Protocol</div>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textSecondary, padding: "4px" }}><IconX /></button>
      </div>

      {/* Step 1: Select Protocol */}
      <div style={{ marginBottom: "14px" }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "6px" }}>1. Select Protocol {errors.protocol && <span style={{ color: COLORS.danger }}>— {errors.protocol}</span>}</div>
        <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: "6px", padding: "0 8px", height: "34px", flex: 1 }}>
            <IconSearch />
            <input type="text" placeholder="Search protocols…" value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ border: "none", outline: "none", background: "transparent", marginLeft: "6px", fontSize: "13px", width: "100%", color: COLORS.text }} />
          </div>
        </div>
        <div style={{ maxHeight: "220px", overflowY: "auto", background: COLORS.surface, borderRadius: "8px", border: `1px solid ${COLORS.border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: "8px 10px", borderBottom: `2px solid ${COLORS.border}`, width: "30px" }} />
                <SortTh field="name" label="Protocol" />
                <SortTh field="treatment_type" label="Type" width="70px" />
                <SortTh field="treatment_ratio_pct" label="Intensity" width="80px" />
                <SortTh field="frequency_hz" label="Freq" width="60px" />
                <SortTh field="total_pulses" label="Pulses" width="70px" />
                <SortTh field="fda_approved" label="FDA" width="45px" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}
                  onClick={() => handleSelectProtocol(p)}
                  style={{
                    borderBottom: `1px solid ${COLORS.borderLight}`, cursor: "pointer",
                    background: selectedProtocol?.id === p.id ? COLORS.primaryLight : "transparent",
                  }}
                  onMouseEnter={(e) => { if (selectedProtocol?.id !== p.id) e.currentTarget.style.background = "#F7F8FA"; }}
                  onMouseLeave={(e) => { if (selectedProtocol?.id !== p.id) e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 10px", textAlign: "center" }}>
                    <div style={{
                      width: "16px", height: "16px", borderRadius: "50%",
                      border: `2px solid ${selectedProtocol?.id === p.id ? COLORS.primary : COLORS.border}`,
                      background: selectedProtocol?.id === p.id ? COLORS.primary : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {selectedProtocol?.id === p.id && <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#FFF" }} />}
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", fontWeight: "500", color: COLORS.text }}>
                    {p.is_system_default && <span style={{ color: COLORS.textSecondary, marginRight: "4px" }}><IconLock /></span>}
                    {p.name}
                    {p.has_safety_warning && <span style={{ color: COLORS.warning, marginLeft: "4px" }}>⚠</span>}
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{ padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "600", background: p.treatment_type === "TBS" ? "#EDE7F6" : COLORS.primaryLight, color: p.treatment_type === "TBS" ? "#6A3DB5" : COLORS.primary }}>
                      {p.treatment_type === "TBS" ? "TBS" : "rTMS"}
                    </span>
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary }}>{p.intensity_is_machine_pct ? `${p.treatment_ratio_pct}% mach` : `${p.treatment_ratio_pct}% MT`}</td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary }}>{p.frequency_hz}Hz</td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", color: COLORS.textSecondary }}>{p.total_pulses.toLocaleString()}</td>
                  <td style={{ padding: "8px 10px", fontSize: "12px", textAlign: "center" }}>{p.fda_approved ? <span style={{ color: COLORS.accent, fontWeight: "700" }}>✓</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selectedProtocol && (
          <div style={{ marginTop: "8px", padding: "10px 12px", background: COLORS.surface, borderRadius: "8px", border: `1px solid ${COLORS.accent}40`, fontSize: "13px" }}>
            <strong style={{ color: COLORS.accent }}>Selected:</strong> {selectedProtocol.name} — {selectedProtocol.treatment_type === "TBS" ? "TBS" : "rTMS"}, {selectedProtocol.frequency_hz}Hz, {selectedProtocol.total_pulses.toLocaleString()} pulses, {selectedProtocol.treatment_coil} → {selectedProtocol.treatment_spot}
          </div>
        )}
      </div>

      {/* Steps 2-5: Coil Configuration */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>2. Treatment Coil {errors.txCoil && <span style={{ color: COLORS.danger }}>*</span>}</div>
          <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.txCoil ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text }}
            value={txCoil} onChange={(e) => { setTxCoil(e.target.value); setErrors((p) => ({ ...p, txCoil: null })); }}>
            <option value="">Select…</option>
            {coils.filter((c) => c.is_active).map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>3. Treatment Spot {errors.txSpot && <span style={{ color: COLORS.danger }}>*</span>}</div>
          <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.txSpot ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text }}
            value={txSpot} onChange={(e) => { setTxSpot(e.target.value); setErrors((p) => ({ ...p, txSpot: null })); }}>
            <option value="">Select…</option>
            {treatmentSpots.filter((s) => s.is_active).map((s) => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>4. MT Coil {errors.mtCoil && <span style={{ color: COLORS.danger }}>*</span>}</div>
          <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.mtCoil ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text }}
            value={mtCoil} onChange={(e) => { setMtCoil(e.target.value); setErrors((p) => ({ ...p, mtCoil: null })); }}>
            <option value="">Select…</option>
            {coils.filter((c) => c.is_active).map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>5. MT Spot {errors.mtSpot && <span style={{ color: COLORS.danger }}>*</span>}</div>
          <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.mtSpot ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text }}
            value={mtSpotVal} onChange={(e) => { setMtSpotVal(e.target.value); setErrors((p) => ({ ...p, mtSpot: null })); }}>
            <option value="">Select…</option>
            {mtSpots.filter((s) => s.is_active).map((s) => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Steps 6-7: Diagnosis + Planned Sessions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>6. Diagnosis (ICD-10) {errors.diagnosis && <span style={{ color: COLORS.danger }}>— {errors.diagnosis}</span>}</div>
          <select style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.diagnosis ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text }}
            value={diagCode} onChange={(e) => { setDiagCode(e.target.value); setErrors((p) => ({ ...p, diagnosis: null })); }}>
            <option value="">Select…</option>
            {diagnoses.filter((d) => d.is_active !== false).map((d) => (
              <option key={d.id || d.code} value={d.code}>{d.code} — {d.description}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "4px" }}>7. Planned Sessions {errors.sessions && <span style={{ color: COLORS.danger }}>— {errors.sessions}</span>}</div>
          <input type="number" min="1" style={{ width: "100%", height: "40px", padding: "0 10px", borderRadius: "8px", border: `1px solid ${errors.sessions ? COLORS.danger : COLORS.border}`, background: COLORS.surface, fontSize: "14px", color: COLORS.text, boxSizing: "border-box" }}
            value={plannedSessions} onChange={(e) => { setPlannedSessions(e.target.value); setErrors((p) => ({ ...p, sessions: null })); }} />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button onClick={handleSubmit}
            style={{ height: "40px", padding: "0 24px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: "pointer", width: "100%" }}>
            Assign Protocol
          </button>
        </div>
      </div>

    </div>
  );
}

// ─── Slide-to-Confirm Component ──────────────────────────────

